import { useState, useEffect } from 'react'
import SidePanel from '@/components/ui/SidePanel'
import Button from '@/components/ui/Button'
import Input from '@/components/ui/Input'
import { designTokens } from '@/utils/design-tokens'
import type { WorkspaceResponse } from '@/api/generated/models/WorkspaceResponse'
import { getWorkspaceShowTasks } from '@/hooks/useShowTasks'

interface WorkspaceEditPanelProps {
  isOpen: boolean
  onClose: () => void
  onSubmit: (workspaceData: {
    name: string
    sortKey?: number
    available: boolean
    startDate?: string | null
    endDate?: string | null
    showTasks: boolean
  }) => void
  workspace: WorkspaceResponse | null
  isLoading?: boolean
}

// Section Header Component
const SectionHeader: React.FC<{ title: string }> = ({ title }) => (
  <div 
    className="pb-2 border-b" 
    style={{ 
      borderBottomColor: '#EBEBEB',
      borderBottomWidth: '1px',
      padding: '7px 0px'
    }}
  >
    <h3 
      style={{
        fontFamily: designTokens.typography.fontFamily.sans[0],
        fontSize: '18px',
        fontWeight: 700,
        color: designTokens.colors.gray[6]
      }}
    >
      {title}
    </h3>
  </div>
)

// Form Field Component
const FormField: React.FC<{ 
  label: string
  required?: boolean
  children: React.ReactNode
}> = ({ label, required = false, children }) => (
  <div className="flex flex-col gap-2">
    <label 
      style={{ 
        fontFamily: designTokens.typography.fontFamily.sans[0],
        fontSize: '16px',
        fontWeight: 400,
        color: designTokens.colors.gray[6]
      }}
    >
      {label}
      {required && (
        <span 
          className="ml-1"
          style={{ 
            fontFamily: designTokens.typography.fontFamily.sans[0],
            fontSize: '14px',
            color: designTokens.colors.error.DEFAULT
          }}
        >
          *
        </span>
      )}
    </label>
    {children}
  </div>
)

export default function WorkspaceEditPanel({
  isOpen,
  onClose,
  onSubmit,
  workspace,
  isLoading = false
}: WorkspaceEditPanelProps) {
  const [workspaceName, setWorkspaceName] = useState('')
  const [sortKey, setSortKey] = useState<string>('')
  const [available, setAvailable] = useState(true)
  const [startDate, setStartDate] = useState<string>('')
  const [endDate, setEndDate] = useState<string>('')
  const [showTasks, setShowTasks] = useState(true)

  // Pre-populate form with existing workspace data
  useEffect(() => {
    if (workspace) {
      setWorkspaceName(workspace.name || '')
      setSortKey(workspace.sort_key?.toString() || '')
      setAvailable(workspace.available ?? true)
      // start_date/end_date는 'YYYY-MM-DD'(또는 ISO) → input[type=date]용으로 앞 10자리만
      setStartDate(workspace.start_date ? workspace.start_date.slice(0, 10) : '')
      setEndDate(workspace.end_date ? workspace.end_date.slice(0, 10) : '')
      setShowTasks(getWorkspaceShowTasks(workspace))
    }
  }, [workspace])


  const handleSubmit = () => {
    if (workspaceName.trim()) {
      const sortKeyNumber = sortKey.trim() ? parseInt(sortKey, 10) : undefined
      onSubmit({
        name: workspaceName.trim(),
        sortKey: !isNaN(sortKeyNumber as number) ? sortKeyNumber : undefined,
        available,
        startDate: startDate.trim() ? startDate : null,
        endDate: endDate.trim() ? endDate : null,
        showTasks
      })
    }
  }

  const handleCancel = () => {
    // Reset form
    if (workspace) {
      setWorkspaceName(workspace.name || '')
      setSortKey(workspace.sort_key?.toString() || '')
      setAvailable(workspace.available ?? true)
      setStartDate(workspace.start_date ? workspace.start_date.slice(0, 10) : '')
      setEndDate(workspace.end_date ? workspace.end_date.slice(0, 10) : '')
      setShowTasks(getWorkspaceShowTasks(workspace))
    }
    onClose()
  }

  const isFormValid = workspaceName.trim().length > 0

  const headerActions = (
    <>
      <Button
        size="small"
        color="white"
        onClick={handleCancel}
        disabled={isLoading}
      >
        취소
      </Button>
      <Button
        size="small"
        color="dark"
        onClick={handleSubmit}
        disabled={isLoading || !isFormValid}
      >
        {isLoading ? '저장 중...' : '저장'}
      </Button>
    </>
  )

  return (
    <SidePanel
      isOpen={isOpen}
      onClose={handleCancel}
      title="워크스페이스 수정"
      width={800}
      headerActions={headerActions}
    >
      <div className="px-6 py-6 space-y-8 overflow-y-auto flex-1">

        {/* 기본 정보 섹션 */}
        <div className="space-y-6">
          <SectionHeader title="기본 정보" />
          <div className="space-y-4">
            {/* 워크스페이스명 */}
            <FormField label="워크스페이스명" required>
              <Input
                placeholder="워크스페이스명을 입력해주세요."
                value={workspaceName}
                onChange={(e) => setWorkspaceName(e.target.value)}
                size="small"
                autoFocus
              />
            </FormField>

            {/* 순서 */}
            <FormField label="순서">
              <Input
                type="number"
                placeholder="순서를 입력해주세요. (선택사항)"
                value={sortKey}
                onChange={(e) => setSortKey(e.target.value)}
                size="small"
                min="1"
              />
            </FormField>

            {/* 시작일 */}
            <FormField label="시작일">
              <Input
                type="date"
                value={startDate}
                onChange={(e) => setStartDate(e.target.value)}
                size="small"
                max={endDate || undefined}
              />
            </FormField>

            {/* 종료일 */}
            <FormField label="종료일">
              <Input
                type="date"
                value={endDate}
                onChange={(e) => setEndDate(e.target.value)}
                size="small"
                min={startDate || undefined}
              />
            </FormField>

            {/* 상태 */}
            <FormField label="상태" required>
              <div className="flex items-center gap-4">
                <label className="flex items-center gap-2 cursor-pointer">
                  <input
                    type="radio"
                    name="status"
                    checked={available}
                    onChange={() => setAvailable(true)}
                    className="w-4 h-4"
                    style={{ accentColor: designTokens.colors.primary.DEFAULT }}
                  />
                  <span 
                    style={{ 
                      fontFamily: designTokens.typography.fontFamily.sans[0],
                      fontSize: '16px',
                      fontWeight: 400,
                      color: designTokens.colors.gray[6]
                    }}
                  >
                    활성
                  </span>
                </label>
                <label className="flex items-center gap-2 cursor-pointer">
                  <input
                    type="radio"
                    name="status"
                    checked={!available}
                    onChange={() => setAvailable(false)}
                    className="w-4 h-4"
                    style={{ accentColor: designTokens.colors.primary.DEFAULT }}
                  />
                  <span 
                    style={{ 
                      fontFamily: designTokens.typography.fontFamily.sans[0],
                      fontSize: '16px',
                      fontWeight: 400,
                      color: designTokens.colors.gray[6]
                    }}
                  >
                    비활성
                  </span>
                </label>
              </div>
            </FormField>

            {/* 세부실행과제 표시 — OFF 면 현황·성과보고서 화면에서 세부실행과제 영역을 숨긴다 */}
            <FormField label="세부실행과제 표시">
              <div className="flex items-center gap-4">
                <label className="flex items-center gap-2 cursor-pointer">
                  <input
                    type="radio"
                    name="showTasks"
                    checked={showTasks}
                    onChange={() => setShowTasks(true)}
                    className="w-4 h-4"
                    style={{ accentColor: designTokens.colors.primary.DEFAULT }}
                  />
                  <span
                    style={{
                      fontFamily: designTokens.typography.fontFamily.sans[0],
                      fontSize: '16px',
                      fontWeight: 400,
                      color: designTokens.colors.gray[6]
                    }}
                  >
                    ON
                  </span>
                </label>
                <label className="flex items-center gap-2 cursor-pointer">
                  <input
                    type="radio"
                    name="showTasks"
                    checked={!showTasks}
                    onChange={() => setShowTasks(false)}
                    className="w-4 h-4"
                    style={{ accentColor: designTokens.colors.primary.DEFAULT }}
                  />
                  <span
                    style={{
                      fontFamily: designTokens.typography.fontFamily.sans[0],
                      fontSize: '16px',
                      fontWeight: 400,
                      color: designTokens.colors.gray[6]
                    }}
                  >
                    OFF
                  </span>
                </label>
              </div>
              <span
                style={{
                  fontFamily: designTokens.typography.fontFamily.sans[0],
                  fontSize: '14px',
                  color: designTokens.colors.gray[5]
                }}
              >
                OFF 로 두면 내 현황·누적 현황·상세 현황·성과보고서에서 세부실행과제 영역이 보이지 않습니다.
              </span>
            </FormField>
          </div>
        </div>

      </div>
    </SidePanel>
  )
}